import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {CdkVirtualScrollViewport, ScrollingModule} from '@angular/cdk/scrolling';

import {ConnectionStatus} from '@common-module/modelinterfaces/enums/connection-status';
import {UnitShort} from '@common-module/modelinterfaces/unit-short.model';
import {NotificationUnread} from '@common-module/modelinterfaces/notification-unread.model';
import {UnitStateStorage} from '@common-module/modelinterfaces/unit-state-storage.model';
import {AuthUserService} from '@common-module/app-services/auth-user.service';
import {NotificationManagerService} from '../../../shared/services/notification-manager.service';
import {SelectedService} from '../../../shared/services/selected.service';
import {UnitStateService} from '../../../shared/services/unit-state.service';
import {UnitStateConst} from "../../../shared/utils/unit-state.const";
import {BaseUnsubscribeComponent} from "@common-module/app-components/base-unsubscribe.component";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {UnitGroupView} from "@common-module/modelinterfaces/user-unit-groups.model";
import {AsyncPipe} from '@angular/common';
import {UnitListOptionComponent} from './unit-list-option/unit-list-option.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-unit-list',
    templateUrl: './unit-list.component.html',
    styleUrls: ['./unit-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCheckbox, ScrollingModule, UnitListOptionComponent, AsyncPipe, TranslateModule]
})

export class UnitListComponent extends BaseUnsubscribeComponent implements OnInit, OnChanges, AfterViewInit {

  // must match .list-option height in unit-list.component.scss
  readonly ROW_HEIGHT = 56;

  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  @Input() isSplitByUsers = true;
  @Input() isPublicInterface = false;
  @Input() units: UnitShort[] = [];
  @Input() unitGroupViews = new Map<number, UnitGroupView>();

  // grouped mode: the viewport gets an explicit px height; flat mode: it stretches to the panel
  @HostBinding('class.grouped') get isGroupedMode(): boolean {
    return this.isSplitByUsers;
  }

  hoveredUnit: UnitShort;

  unitIdSet = new Set<number>();
  selectedUnitIds = new Set<number>();
  groupViewportHeight = 0;

  public connectionStatus = ConnectionStatus;
  public isSelectAll$ = new BehaviorSubject<boolean>(false);
  public stopSpeed: number;
  public timeZone$: Observable<string>;
  public unitsState$: Observable<UnitStateStorage>;
  public noticeDataCount$: Observable<Map<number, NotificationUnread>>;

  constructor(private authUserService: AuthUserService,
              private notificationManagerService: NotificationManagerService,
              private selectedService: SelectedService,
              private unitStateService: UnitStateService,
              private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.units) {
      this.unitIdSet = new Set(this.units.map(unit => unit.id));
      this.refreshSelection(this.selectedService.getInstantUnitIdsSet());
      this.groupViewportHeight = Math.min(this.units.length * this.ROW_HEIGHT, Math.round(window.innerHeight / 2));
    }
  }

  ngOnInit(): void {
    this.timeZone$ = this.authUserService.currentUser$.pipe(map(user => user?.getUserTimeZone()));
    this.unitsState$ = this.unitStateService.unitsStateWithCheck$.pipe(map(data => data?.unitsState));
    this.selectedService.selectedUnitIdsSet$.pipe(takeUntil(this.destroy))
      .subscribe(set => {
        this.refreshSelection(set);
        this.cdr.markForCheck();
      });
    this.noticeDataCount$ = this.notificationManagerService.noticeCount$.pipe(map(counts => counts?.data));
    this.stopSpeed = UnitStateConst.STOP_SPEED;
  }

  ngAfterViewInit(): void {
    // a viewport created inside an expanding mat-expansion-panel can measure a
    // 0-height container; re-measure after the expand animation settles
    if (this.isSplitByUsers) {
      setTimeout(() => this.viewport?.checkViewportSize(), 300);
    }
  }

  public onRowClick(unit: UnitShort): void {
    const wasSelected = this.selectedUnitIds.has(unit.id);
    this.selectedService.removeSelectedUnitId();
    if (wasSelected) {
      this.selectedService.changeLastSelectedUnit(null);
    } else {
      this.selectedService.changeLastSelectedUnit(unit);
      this.selectedService.appendSelectedUnitIds([unit.id]);
    }
  }

  public onCheckboxToggle(unit: UnitShort, $event: MatCheckboxChange): void {
    if ($event.checked) {
      this.selectedService.changeLastSelectedUnit(unit);
      this.selectedService.appendSelectedUnitIds([unit.id]);
    } else {
      this.selectedService.removeSelectedUnitId(unit.id);
      this.selectedService.changeLastSelectedUnit(null);
    }
  }

  public helperSelection($event: MatCheckboxChange): void {
    $event.checked ? this.selectAllUnits() : this.deselectAllUnits();
  }

  private selectAllUnits(): void {
    this.selectedService.appendSelectedUnitIds(this.units.map(unit => unit.id));
    this.selectedService.changeLastSelectedUnit(this.units[this.units.length - 1]);
  }

  private deselectAllUnits(): void {
    this.selectedService.removeSelectedUnitId();
    this.selectedService.changeLastSelectedUnit(null);
  }

  trackById(index: number, item: UnitShort): number {
    return item.id;
  }

  private refreshSelection(set: Set<number>): void {
    this.selectedUnitIds = new Set([...set].filter(unitId => this.unitIdSet.has(unitId)));
    this.isSelectAll$.next(set.size > 0 && this.units.every(el => set.has(el.id)));
  }
}
